<script setup>
import { RouterLink, useRouter } from 'vue-router'
import { CheckIcon, ShieldCheckIcon } from '@heroicons/vue/24/outline'
import { SERVICES, getPlanByServiceId } from '@/utils/constants/services'

/**
 * ServicePricingCards - Componente reutilizable para mostrar servicios con pricing
 *
 * Usado en:
 * - LandingPage (Home) - sección de pricing
 * - Pricing.vue - cards de planes (con autenticación)
 * - ServiceCatalog.vue - grid de servicios
 *
 * Props permiten personalizar:
 * - Mostrar/ocultar descripción
 * - Límite de features visibles
 * - Texto del CTA y enlace a detalle del servicio
 * - Mostrar garantía
 * - Modo autenticación (para upgrades)
 */

const props = defineProps({
  // Mostrar descripción larga
  showDescription: {
    type: Boolean,
    default: true
  },
  // Número de features a mostrar (el resto se muestra como "+X más")
  featuresLimit: {
    type: Number,
    default: 3
  },
  // Texto del botón CTA
  ctaText: {
    type: String,
    default: 'Comenzar'
  },
  // Ruta base para el CTA (se añade ?plan=tier)
  ctaRoute: {
    type: String,
    default: '/pricing'
  },
  // Si true, el CTA lleva directamente a registro con el plan
  directToRegister: {
    type: Boolean,
    default: false
  },
  // Mostrar enlace a página de detalle del servicio
  showDetailLink: {
    type: Boolean,
    default: true
  },
  // Mostrar sección de garantía al final
  showGuarantee: {
    type: Boolean,
    default: true
  },
  // Título de la sección (opcional)
  sectionTitle: {
    type: String,
    default: ''
  },
  // Subtítulo de la sección (opcional)
  sectionSubtitle: {
    type: String,
    default: ''
  },
  // Modo compacto (sin padding vertical)
  compact: {
    type: Boolean,
    default: false
  },
  // === Props de autenticacion ===
  // Si esta autenticado
  isAuthenticated: {
    type: Boolean,
    default: false
  },
  // Tier actual del usuario (olvido, olvido_postmortem, testamento)
  userTier: {
    type: String,
    default: null
  },
  // Si está cargando una acción
  loading: {
    type: Boolean,
    default: false
  },
  // Tier seleccionado actualmente (para mostrar spinner)
  selectedTier: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['select-plan'])
const router = useRouter()

// Usar SERVICES como fuente de datos con datos derivados pre-calculados
const services = SERVICES.map(service => {
  const plan = getPlanByServiceId(service.id)
  return {
    ...service,
    idealFor: plan?.idealFor || ''
  }
})

// Manejar click en CTA
const handleCtaClick = (service) => {
  if (props.loading) return

  if (props.isAuthenticated) {
    // Emitir evento para que el padre maneje el upgrade
    emit('select-plan', service.tier)
  } else {
    // Navegar a registro con el plan
    router.push({
      name: 'Register',
      query: { plan: service.tier }
    })
  }
}

// Generar ruta del CTA según configuración (para RouterLink cuando no hay auth)
const getCtaRoute = (service) => {
  if (props.directToRegister) {
    return `/auth/register?plan=${service.tier}`
  }
  return props.ctaRoute
}

</script>

<template>
  <section :class="['bg-bg-primary', compact ? 'py-0' : 'py-24']">
    <div class="max-w-6xl mx-auto px-6">
      <!-- Section Header (opcional) -->
      <div v-if="sectionTitle || sectionSubtitle" class="text-center mb-16">
        <span v-if="sectionTitle" class="text-sm font-semibold text-accent-600 uppercase tracking-widest mb-4 block">
          Precios
        </span>
        <h2 v-if="sectionTitle" class="text-4xl md:text-5xl font-bold text-text-primary mb-6">
          {{ sectionTitle }}
        </h2>
        <p v-if="sectionSubtitle" class="text-xl text-text-secondary max-w-xl mx-auto">
          {{ sectionSubtitle }}
        </p>
      </div>

      <!-- Service Cards Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Card para cada servicio -->
        <div
          v-for="(service, index) in services"
          :key="service.id"
          class="rounded-2xl p-8 transition-[border-color,box-shadow] bg-white border border-slate-200 hover:border-accent-300 hover:shadow-lg"
        >
          <!-- Header: Nombre + Subtítulo -->
          <div class="mb-6">
            <span class="text-sm font-medium uppercase tracking-wider text-text-tertiary">
              {{ service.name }}
            </span>
            <p v-if="service.shortDescription" class="text-sm mt-1 text-text-secondary">
              {{ service.shortDescription }}
            </p>
          </div>

          <!-- Precio -->
          <div class="mb-6">
            <div class="flex items-baseline gap-1">
              <span class="text-5xl font-bold text-text-primary">
                {{ service.price }}
              </span>
              <span class="text-2xl text-text-tertiary">€</span>
            </div>
            <p class="mt-2 text-text-tertiary">
              Pago único
            </p>
            <!-- Ideal para -->
            <p v-if="service.idealFor" class="mt-3 text-sm text-accent-600 font-medium">
              Ideal para: {{ service.idealFor }}
            </p>
          </div>

          <!-- Descripción (opcional) -->
          <p
            v-if="showDescription && service.description"
            class="text-sm mb-6 leading-relaxed text-text-secondary"
          >
            {{ service.description }}
          </p>

          <!-- Features -->
          <div class="mb-8">
            <h4 class="text-sm font-semibold mb-3 text-text-primary">
              Incluye:
            </h4>
            <ul class="space-y-3">
              <li
                v-for="feature in service.features.slice(0, featuresLimit)"
                :key="feature"
                class="flex items-start gap-3"
              >
                <CheckIcon class="w-5 h-5 mt-0.5 flex-shrink-0 text-accent-500" />
                <span class="text-sm text-text-secondary">
                  {{ feature }}
                </span>
              </li>
              <!-- Indicador de más features -->
              <li
                v-if="service.features.length > featuresLimit"
                class="text-sm font-medium pl-8 text-accent-600"
              >
                + {{ service.features.length - featuresLimit }} funciones más
              </li>
            </ul>
          </div>

          <!-- CTA Button -->
          <!-- Modo sin autenticación: usa RouterLink -->
          <RouterLink
            v-if="!isAuthenticated"
            :to="getCtaRoute(service)"
            class="block w-full py-4 text-center font-semibold rounded-2xl transition-all cursor-pointer border-2 border-slate-200 text-text-primary hover:border-accent-500 hover:text-accent-600"
          >
            {{ ctaText }}
          </RouterLink>

          <!-- Modo con autenticación: usa button -->
          <button
            v-else
            @click="handleCtaClick(service)"
            :disabled="loading"
            :class="[
              'block w-full py-4 text-center font-semibold rounded-2xl transition-all cursor-pointer border-2 border-slate-200 text-text-primary hover:border-accent-500 hover:text-accent-600',
              loading && selectedTier === service.tier ? 'opacity-50' : ''
            ]"
          >
            <span v-if="loading && selectedTier === service.tier" class="flex items-center justify-center gap-2">
              <div class="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
              Procesando...
            </span>
            <span v-else>{{ ctaText }}</span>
          </button>

          <!-- Enlace secundario a página de detalle del servicio -->
          <RouterLink
            v-if="showDetailLink"
            :to="`/services/${service.id}`"
            class="block w-full mt-3 py-2 text-center text-sm font-medium text-text-secondary hover:text-accent-600 focus-visible:text-accent-600 focus-visible:underline transition-colors"
            :aria-label="`Más información sobre ${service.name}`"
          >
            Más información
          </RouterLink>
        </div>
      </div>

      <!-- Garantía (opcional) -->
      <div v-if="showGuarantee" class="mt-12 mb-16 text-center">
        <div class="inline-flex items-center gap-3 px-6 py-4 bg-green-50 border border-green-200 rounded-2xl">
          <ShieldCheckIcon class="w-6 h-6 text-green-600" />
          <span class="text-green-800 font-medium">
            Cumplimiento legal garantizado Art. 17 GDPR
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@reference "../../assets/styles/main.css";
</style>
